import { Button } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import lightGallery from "lightgallery";
import { InitDetail } from "lightgallery/lg-events";
import { CSSProperties, HTMLAttributes, useRef } from "react";

import { AssetUrl } from "@holibob-packages/vault";

import { FavouriteButton } from "../../components/FavouriteButton";
import { LightGalleryImage } from "../../components/LightGalleryImage";
import { ResponsiveImage, ResponsiveImageDimensionDefinitionSizes } from "../../components/ResponsiveImage";
import { ProductGalleryV2Wrapper, DEFAULT_HEIGHT, Mode } from "./ProductGalleryV2Wrapper";

export type ProductGalleryV2Props = {
    imageList: AssetUrl[];
    seeAllLabel: string | undefined;
    height?: number;
    showFavouriteButton?: boolean;
    maxWidthOnDesktop: number;
    isFavourite?: boolean;
    borderRounded?: boolean;
    onFavouriteChange?: (isFavourite: boolean) => void;
} & HTMLAttributes<HTMLDivElement>;

export function ProductGalleryV2({
    imageList,
    style,
    height = DEFAULT_HEIGHT,
    seeAllLabel,
    showFavouriteButton,
    maxWidthOnDesktop,
    isFavourite,
    onFavouriteChange,
    borderRounded = true,
    ...props
}: ProductGalleryV2Props) {
    const ref = useRef<ReturnType<typeof lightGallery> | undefined>(undefined);

    const openImage = (index: number) => {
        const instance = ref.current;
        if (instance) {
            instance.openGallery(index);
        }
    };

    const { mode, displayShowAllImagesDesktop, displayShowAllImagesMobile } = useGalleryOptions(imageList);

    const rootStyle = { "--product-gallery-height": height + "px", ...style } as CSSProperties;

    function onImageGalleryInit(detail: InitDetail) {
        ref.current = detail.instance;
    }

    return (
        <>
            <ProductGalleryV2Wrapper data-mode={mode} style={rootStyle} data-is-rounded={borderRounded} {...props}>
                {imageList.slice(0, 5).map((x, index) => {
                    return (
                        <GalleryImage
                            image={x}
                            index={index}
                            mode={mode}
                            baseHeight={height}
                            maxContainerWidthOnDesktop={maxWidthOnDesktop}
                            key={index}
                            onClick={() => {
                                openImage(index);
                            }}
                        />
                    );
                })}

                <SeeAllButtonWrapper
                    aria-hidden={!seeAllLabel}
                    data-is-visible-on-desktop={displayShowAllImagesDesktop}
                    data-is-visible-on-mobile={displayShowAllImagesMobile}
                >
                    <SeeAllButton
                        data-ref="see-all-button"
                        data-leave-me-alone={true}
                        color="secondary"
                        variant="outlined"
                        size="medium"
                        onClick={() => {
                            openImage(0);
                        }}
                    >
                        {seeAllLabel}
                    </SeeAllButton>
                </SeeAllButtonWrapper>
                {showFavouriteButton && (
                    <FavouriteButtonWrapper>
                        <FavouriteButton
                            data-testid="favourite-button"
                            size="medium"
                            isFavourite={isFavourite ?? false}
                            onChange={onFavouriteChange}
                            color="light"
                        />
                    </FavouriteButtonWrapper>
                )}
            </ProductGalleryV2Wrapper>
            <LightGalleryImage assetUrlList={imageList} onInit={onImageGalleryInit} />
        </>
    );
}

const SeeAllButtonWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    alignSelf: "end",
    justifySelf: "start",
    gridArea: "first",
    margin: theme.spacing(2),
    position: "relative", // This is required, otherwise hover on button makes wrapper disappear
    '&&[aria-hidden="true"]': {
        display: "none",
    },
    '&[data-is-visible-on-mobile="false"]': {
        display: "none",
    },
    [theme.breakpoints.up("md")]: {
        '&[data-is-visible-on-desktop="false"]': {
            display: "none",
        },
    },
}));
const SeeAllButton = styled(Button)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
}));

function useGalleryOptions(imageList: AssetUrl[]) {
    let mode: Mode = Mode.SINGLE;
    let displayShowAllImagesDesktop = false;
    if (imageList.length >= 5) {
        mode = Mode.SPLIT_TO_4;
        displayShowAllImagesDesktop = imageList.length > 5;
    } else if (imageList.length >= 3) {
        mode = Mode.SPLIT_TO_2;
        displayShowAllImagesDesktop = imageList.length > 3;
    } else if (imageList.length === 2) {
        displayShowAllImagesDesktop = true;
    }
    return {
        mode,
        displayShowAllImagesDesktop,
        displayShowAllImagesMobile: imageList.length > 1,
    };
}

type GalleryImageProps = {
    image: AssetUrl;
    index: number;
    mode: Mode;
    baseHeight: number;
    maxContainerWidthOnDesktop: number;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

export function GalleryImage({
    image,
    index,
    baseHeight,
    mode,
    maxContainerWidthOnDesktop,
    ...props
}: GalleryImageProps) {
    const imageProps = useImageProps(index, mode, maxContainerWidthOnDesktop);
    return (
        <ImageWrapper data-ref={"image"} {...props} data-testid="image-wrapper">
            <ResponsiveImageStyled
                src={image}
                alt=""
                height={index === 0 ? baseHeight : baseHeight / 2}
                loading={index === 0 ? "eager" : "lazy"}
                {...imageProps}
            />
        </ImageWrapper>
    );
}

const ImageWrapper = styled("button")(({ theme }) => ({
    display: "block",
    border: 0,
    background: "transparent",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    opacity: 1,
    transition: theme.transitions.create("opacity", {
        duration: theme.transitions.duration.short,
    }),
    "&:hover": {
        opacity: 0.8,
    },
}));

const ResponsiveImageStyled = styled(ResponsiveImage)({
    img: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
    },
});

function useImageProps(
    index: number,
    mode: Mode,
    maxContainerWidthOnDesktop: number
): ResponsiveImageDimensionDefinitionSizes {
    const theme = useTheme();
    let sizes = `(min-width: ${theme.breakpoints.values.md}px) ${maxContainerWidthOnDesktop}px, 100vw`;
    if (mode === Mode.SPLIT_TO_2) {
        if (index === 0) {
            const widthOnDesktop = Math.ceil(maxContainerWidthOnDesktop * 0.6);
            sizes = `(min-width: ${theme.breakpoints.values.md}px) ${widthOnDesktop}px, 100vw`;
        } else {
            sizes = Math.ceil(maxContainerWidthOnDesktop * 0.4) + "px";
        }
    } else if (mode === Mode.SPLIT_TO_4) {
        if (index === 0) {
            const widthOnDesktop = Math.ceil(maxContainerWidthOnDesktop * 0.5);
            sizes = `(min-width: ${theme.breakpoints.values.md}px) ${widthOnDesktop}px, 100vw`;
        } else {
            sizes = Math.ceil(maxContainerWidthOnDesktop * 0.25) + "px";
        }
    }
    return {
        srcSetSizes: [1400, 1200, 1000, 800, 600, 300].flatMap((x) => {
            return [x * 2, x * 1.5, x];
        }),
        sizes: sizes,
    };
}

const FavouriteButtonWrapper = styled("div")(({ theme }) => ({
    position: "absolute",
    top: theme.spacing(1),
    left: theme.spacing(1),
}));
